import React, { Component } from 'react'
import { withSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Tab, Tabs, TextField, Tooltip, withStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import Service from '../../config/networkutils';
import Auth from '../../utils/authenticate';
import ResourceProfileDivision from './ResourceProfileDivision';
import EditIcon from '@material-ui/icons/Edit';

const token = Auth.token()

const ResourceProfileTabs = withStyles({
    root: {
        minHeight: 48,
        backgroundColor: '#f4f6f8',
        borderBottom: '1px solid #d9e0e5',
    },
    indicator: {
        height: 3,
        borderRadius: '3px 3px 0 0',
        backgroundColor: '#00796b',
    },
})(Tabs);

const ResourceProfileTab = withStyles({
    root: {
        minWidth: 120,
        minHeight: 48,
        padding: '10px 20px',
        color: '#58636e',
        fontSize: 14,
        fontWeight: 600,
        textTransform: 'none',
        transition: 'color 150ms ease, background-color 150ms ease',
        '&:hover': {
            color: '#005f56',
            backgroundColor: 'rgba(0, 121, 107, 0.06)',
        },
        '&$selected': {
            color: '#00695c',
        },
        '&:focus-visible': {
            outline: '2px solid #00796b',
            outlineOffset: -2,
        },
    },
    selected: {},
})(Tab);

class ResourceProfile extends Component {

    constructor(props) {
        super(props);
        this.state = {
            data: [],
            isLoading: true,
            disabled: false,
            selectedTab: 0,
            selectedDivision: null,
            tableData: [],
            isTableLoading: false,
            isAddDialogOpen: false,
            newItemName: '',
            isEditDialogOpen: false,
            editStageName: '',
            isDeleteStageDialogOpen: false,
            isDeletingStage: false,
            isProjectDialogOpen: false,
            projectType: '',
            projectValue: '',
            projectDivision: null,
            projectDiscipline: null,
            projectFormSubmitted: false,
            divisionData: [],
            workTypeData: [],
            selectedStage: null,
            disciplineData: []
        };
    }

    handleAddDialogClose = () => {
        this.setState({ isAddDialogOpen: false, newItemName: '' });
    }

    handleEditDialogClose = () => {
        this.setState({ isEditDialogOpen: false, editStageName: '' });
    }

    openEditDialog = () => {
        const selectedStage = this.state.data[this.state.selectedTab];
        if (!selectedStage) return;
        this.setState({ isEditDialogOpen: true, editStageName: selectedStage.name || '' });
    }

    handleEditSubmit = event => {
        event.preventDefault();
        if (!this.state.editStageName.trim()) {
            this.props.enqueueSnackbar('Please enter a resource profile stage name.', {
                variant: 'warning'
            });
            return;
        }
        this.updateStage();
    }

    openDeleteStageDialog = () => {
        if (!this.state.data[this.state.selectedTab]) return;
        this.setState({ isDeleteStageDialogOpen: true });
    }

    handleDeleteStageDialogClose = () => {
        if (!this.state.isDeletingStage) {
            this.setState({ isDeleteStageDialogOpen: false });
        }
    }

    openProjectDialog = () => {
        const scotlandDivision = this.state.divisionData.find(division => (
            (division?.name || division?.division_name || division?.label || '').trim().toLowerCase() === 'scotland'
        )) || null;
        this.setState({
            isProjectDialogOpen: true,
            projectType: '',
            projectValue: '',
            projectDivision: scotlandDivision,
            projectDiscipline: null,
            projectFormSubmitted: false
        });
    }

    handleProjectDialogClose = () => {
        this.setState({
            isProjectDialogOpen: false,
            projectDiscipline: null,
            projectFormSubmitted: false
        });
    }

    handleProjectSubmit = event => {
        event.preventDefault();
        this.setState({ projectFormSubmitted: true });

        if (!this.state.projectType.trim() || !this.state.projectValue.trim() || !this.state.projectDivision || !this.state.projectDiscipline) {
            this.props.enqueueSnackbar('Please complete all required fields.', {
                variant: 'warning'
            });
        }
    }

    getDivisionId = selectedDivision => {
        if (selectedDivision === null || selectedDivision === undefined) return null;
        if (typeof selectedDivision !== 'object') return selectedDivision;

        const division = selectedDivision.division;
        return division?.id
            ?? division?.division_id
            ?? (typeof division !== 'object' ? division : null)
            ?? selectedDivision.division_id
            ?? selectedDivision.id
            ?? null;
    }

    refreshSelectedDivisionTable = () => {
        const division = this.getDivisionId(this.state.selectedDivision);

        if (division !== null) {
            this.getResourceProfileTableData(division)
        }
    }

    handleDivisionChange = (event, selectedDivision) => {
        this.setState({ selectedDivision, tableData: [] }, () => {
            if (selectedDivision) {
                const divisionId = this.getDivisionId(selectedDivision);
                if (divisionId !== null) {
                    this.getResourceProfileTableData(divisionId);
                } else {
                    this.setState({ isTableLoading: false });
                }
            } else {
                this.setState({ isTableLoading: false });
            }
        });
    }

    handleAddSubmit = event => {
        event.preventDefault();
        if (!this.state.newItemName.trim()) {
            this.props.enqueueSnackbar('Please enter a resource profile stage name.', {
                variant: 'warning'
            });
            return;
        }
        this.addStage();
    }

    getData() {
        Service.get(`/api/resource/profile/stage/get_resource_profile_stage_data/`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                this.setState({ data: res.data, isLoading: false });
            })
            .catch(error => {
                console.log(error);
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    getResourceProfileTableData(division) {
        const divisionId = this.getDivisionId(division);
        const resourceProfileStageId = this.state.data[this.state.selectedTab]?.id;
        if (divisionId === null || resourceProfileStageId === null || resourceProfileStageId === undefined) {
            this.setState({ isTableLoading: false, tableData: [] });
            return;
        }

        this.setState({ isTableLoading: true, tableData: [] });
        const params = new URLSearchParams({
            division: String(divisionId),
            resource_profile_stage: String(resourceProfileStageId),
        });
        Service.get(`/api/resource/profile/role/quantity/get_resource_stage_demand_data/?${params.toString()}`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                const tableData = Array.isArray(res.data)
                    ? res.data
                    : res.data
                        ? [res.data]
                        : [];
                this.setState({ tableData, isTableLoading: false });
            })
            .catch(error => {
                console.log(error);
                this.setState({ isTableLoading: false });
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    getDivision() {
        Service.get(`/api/marketsubvertical/get_market_subvertical_data/`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                const divisionData = res.data || [];
                const scotlandDivision = divisionData.find(division => (
                    (division?.name || '').includes('Scotland')
                )) || null;
                this.setState(prevState => ({
                    divisionData,
                    projectDivision: prevState.projectDivision || scotlandDivision
                }));
            })
            .catch(error => {
                console.log(error);
                this.setState({ divisionData: [] });
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    getWorkType() {
        Service.get(`/api/role/worktype/role_work_type_link/`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                this.setState({ workTypeData: res.data || [] });
            })
            .catch(error => {
                console.log(error);
                this.setState({ workTypeData: [] });
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    getDiscipline() {
        Service.get(`/api/subsector/get_with_delete_status/`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                this.setState({ disciplineData: res.data || [] });
            })
            .catch(error => {
                console.log(error);
                this.setState({ disciplineData: [] });
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    componentDidMount() {
        this.getDivision()
        this.getData()
        this.getWorkType()
        this.getDiscipline()
    }

    addStage = () => {
        Service.post(`/api/resource/profile/stage/create_resource_profile_stage_data/`, {
            headers: {
                Authorization: "Token " + token,
            },
            data: {
                name: this.state.newItemName,
            }
        })
            .then(res => {
                this.props.enqueueSnackbar('Resource profile stage added successfully!', {
                    variant: 'success'
                });
                this.setState({
                    isLoading: true,
                    isAddDialogOpen: false
                }, () => {
                    this.getData()
                })
            }).catch(error => {
                console.log(error)
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            })
    }

    updateStage = () => {
        const selectedStage = this.state.data[this.state.selectedTab];
        if (!selectedStage) return;

        Service.patch(`/api/resource/profile/stage/update_resource_profile_stage_data/`, {
            headers: {
                Authorization: "Token " + token,
            },
            data: {
                id: selectedStage.id,
                name: this.state.editStageName.trim()
            }
        })
            .then(() => {
                this.props.enqueueSnackbar('Resource profile stage updated successfully!', {
                    variant: 'success'
                });
                this.setState({ isEditDialogOpen: false, editStageName: '', isLoading: true }, () => {
                    this.getData();
                });
            }).catch(error => {
                console.log(error);
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    deleteStage = () => {
        const selectedStage = this.state.data[this.state.selectedTab];
        if (!selectedStage || this.state.isDeletingStage) return;

        this.setState({ isDeletingStage: true });
        Service.delete(`/api/resource/profile/stage/delete_resource_profile_stage_data/?id=${selectedStage.id}`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(() => {
                const selectedTab = Math.max(0, Math.min(this.state.selectedTab, this.state.data.length - 2));
                this.props.enqueueSnackbar('Resource profile stage deleted successfully!', {
                    variant: 'success'
                });
                this.setState({
                    isDeleteStageDialogOpen: false,
                    isDeletingStage: false,
                    selectedTab,
                    selectedDivision: null,
                    tableData: [],
                    isTableLoading: false,
                    isLoading: true,
                }, () => this.getData());
            }).catch(error => {
                console.log(error);
                this.setState({ isDeletingStage: false });
                this.props.enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    handleProjectDialogSubmit = () => {
        if (!this.state.projectType) {
            this.props.enqueueSnackbar('Project type is required!', {
                variant: 'warning'
            });
            return;
        }
        else if (!this.state.projectValue) {
            this.props.enqueueSnackbar('Project value is required!', {
                variant: 'warning'
            });
            return;
        } else if (!this.state.projectDivision) {
            this.props.enqueueSnackbar('Project division is required!', {
                variant: 'warning'
            });
            return;
        } else if (!this.state.projectDiscipline) {
            this.props.enqueueSnackbar('Project discipline is required!', {
                variant: 'warning'
            });
            return;
        } else {
            Service.post(`/api/resource/profile/project/type/create_resource_profile_project_type_data/`, {
                headers: {
                    Authorization: "Token " + token,
                },
                data: {
                    division: this.state.projectDivision.id,
                    descipline: this.state.projectDiscipline.id,
                    resource_profile_stage: this.state.data[this.state.selectedTab].id,
                    project_value: this.state.projectValue,
                    project_type: this.state.projectType
                }
            })
                .then(res => {
                    const projectDivision = this.state.projectDivision;
                    this.props.enqueueSnackbar('Resource profile project type added successfully!', {
                        variant: 'success'
                    });
                    this.setState({
                        selectedDivision: projectDivision,
                        isProjectDialogOpen: false,
                        projectType: '',
                        projectValue: '',
                        projectDivision: null,
                        projectDiscipline: null,
                        projectFormSubmitted: false
                    }, this.refreshSelectedDivisionTable);
                }).catch(error => {
                    console.log(error)
                    this.props.enqueueSnackbar('Something went wrong!', {
                        variant: 'error'
                    });
                })
        }
    }

    handleEditResourceProfileStage() {
        if (this.state.editStageName.length === 0) {
            this.props.enqueueSnackbar('Stage name is required!', {
                variant: 'error'
            });
            return;
        } else {
            this.setState({
                disabled: true,
            })
            Service.patch(`/api/resource/profile/stage/update_resource_profile_stage_data/`, {
                headers: {
                    Authorization: "Token " + token,
                },
                data: {
                    id: this.state.editStageId,
                    stage_name: this.state.editStageName
                }
            })
                .then(res => {
                    const projectDivision = this.state.projectDivision;
                    this.props.enqueueSnackbar('Resource profile project type edited successfully!', {
                        variant: 'success'
                    });
                    this.setState({
                        selectedDivision: projectDivision,
                        isProjectDialogOpen: false,
                        projectType: '',
                        projectValue: '',
                        projectDivision: null,
                        projectFormSubmitted: false,
                        disabled: false
                    })
                    this.getResourceProfileTableData(projectDivision.id);
                }).catch(error => {
                    console.log(error)
                    this.setState({
                        disabled: false
                    })
                    this.props.enqueueSnackbar('Something went wrong!', {
                        variant: 'error'
                    });
                })
        }

    }

    handleAddDialogSubmit(){
        if (this.state.newItemName.trim().length === 0) {
            this.props.enqueueSnackbar('Item name is required!', {
                variant: 'error'
            });
            return;
        }

        Service.post(`/api/resource/profile/stage/add_resource_profile_stage/`, {
            headers: {
                Authorization: "Token " + token,
            },
            data: {
                name: this.state.newItemName.trim()
            }
        })
        .then(res => {
            this.props.enqueueSnackbar('Resource profile stage added successfully!', {
                variant: 'success'
            });
            this.setState({
                isAddDialogOpen: false,
                newItemName: ''
            });
            this.getResourceProfileStages();
        }).catch(error => {
            console.log(error)
            this.props.enqueueSnackbar('Something went wrong!', {
                variant: 'error'
            });
        });
    }

    render() {
        const projectTypeMissing = this.state.projectFormSubmitted && !this.state.projectType.trim();
        const projectValueMissing = this.state.projectFormSubmitted && !this.state.projectValue.trim();
        const projectDivisionMissing = this.state.projectFormSubmitted && !this.state.projectDivision;

        return (
            <div>
                {this.state.isLoading ? (
                    <Box display='flex' justifyContent='center' alignItems='center' width='100%'><CircularProgress /></Box>
                ) : (
                    <Box>
                        <Box display='flex' alignItems='center' width='100%'>
                            <Box style={{ flex: '0 0 70%', minWidth: 0 }}>
                                <ResourceProfileTabs
                                    value={this.state.selectedTab}
                                    onChange={(event, selectedTab) => this.setState({
                                        selectedTab,
                                        selectedDivision: null,
                                        tableData: [],
                                        isTableLoading: false
                                    })}
                                    variant='scrollable'
                                    scrollButtons='auto'
                                    aria-label='Resource profile tabs'
                                >
                                    {this.state.data.map((item, index) => (
                                        <ResourceProfileTab key={index} label={item.name} />
                                    ))}
                                </ResourceProfileTabs>
                            </Box>
                            <Tooltip title='Add resource profile stage'>
                                <IconButton
                                    style={{ flexShrink: 0 }}
                                    color='primary'
                                    aria-label='Add resource profile stage'
                                    onClick={() => this.setState({ isAddDialogOpen: true })}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Edit selected resource profile stage'>
                                <span>
                                    <IconButton
                                        style={{ flexShrink: 0 }}
                                        color='primary'
                                        aria-label='Edit selected resource profile stage'
                                        onClick={this.openEditDialog}
                                        disabled={!this.state.data[this.state.selectedTab]}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title='Delete selected resource profile stage'>
                                <span>
                                    <IconButton
                                        style={{ flexShrink: 0 }}
                                        color='primary'
                                        aria-label='Delete selected resource profile stage'
                                        onClick={this.openDeleteStageDialog}
                                        disabled={!this.state.data[this.state.selectedTab] || this.state.isDeletingStage}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Divider orientation='vertical' flexItem style={{ margin: '8px 12px', borderLeftWidth: 2 }} />
                            <Box flexGrow={1} minWidth={0} mr={1}>
                                <Autocomplete
                                    options={this.state.divisionData || []}
                                    getOptionLabel={option => option?.name || ''}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    value={this.state.selectedDivision}
                                    onChange={this.handleDivisionChange}
                                    renderInput={params => <TextField {...params} label='Division' variant='outlined' size='small' fullWidth />}
                                />
                            </Box>
                            <Divider orientation='vertical' flexItem style={{ margin: '8px 8px', borderLeftWidth: 2 }} />
                            <Tooltip title='Add project type'>
                                <IconButton
                                    style={{ flexShrink: 0 }}
                                    color='primary'
                                    aria-label='Add project type'
                                    onClick={this.openProjectDialog}
                                    disabled={!this.state.selectedDivision}
                                >
                                    <AddBoxIcon />
                                </IconButton>
                            </Tooltip>

                        </Box>
                        <Box mt={2}>
                            <ResourceProfileDivision
                                selectedStage={this.state.data[this.state.selectedTab] || null}
                                tableData={this.state.tableData}
                                isTableLoading={this.state.isTableLoading}
                                selectedDivision={this.state.selectedDivision}
                                workTypeData={this.state.workTypeData}
                                disciplineData={this.state.disciplineData}
                                refreshTable={this.refreshSelectedDivisionTable}
                            />
                        </Box>
                        <Dialog
                            open={this.state.isAddDialogOpen}
                            onClose={this.handleAddDialogClose}
                            aria-labelledby='add-resource-profile-stage-title'
                            fullWidth
                            maxWidth='xs'
                        >
                                <DialogTitle id='add-resource-profile-stage-title'>Add Resource Profile Stage</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        label='Name'
                                        value={this.state.newItemName}
                                        onChange={event => this.setState({ newItemName: event.target.value })}
                                        margin='dense'
                                        variant='outlined'
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={this.handleAddDialogClose} color='default'>Cancel</Button>
                                    <Button type='submit' color='primary' variant='contained' onClick={this.handleAddSubmit}>
                                        Submit
                                    </Button>
                                </DialogActions>
                        </Dialog>
                        <Dialog
                            open={this.state.isDeleteStageDialogOpen}
                            onClose={this.handleDeleteStageDialogClose}
                            aria-labelledby='delete-resource-profile-stage-title'
                            aria-describedby='delete-resource-profile-stage-description'
                            fullWidth
                            maxWidth='xs'
                        >
                            <DialogTitle id='delete-resource-profile-stage-title'>Delete Resource Profile Stage</DialogTitle>
                            <DialogContent id='delete-resource-profile-stage-description'>
                                Are you sure you want to delete {this.state.data[this.state.selectedTab]?.name || 'this stage'}?
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.handleDeleteStageDialogClose} disabled={this.state.isDeletingStage}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={this.deleteStage}
                                    color='secondary'
                                    variant='contained'
                                    disabled={this.state.isDeletingStage}
                                >
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog
                            open={this.state.isEditDialogOpen}
                            onClose={this.handleEditDialogClose}
                            aria-labelledby='edit-resource-profile-stage-title'
                            fullWidth
                            maxWidth='xs'
                        >
                                <DialogTitle id='edit-resource-profile-stage-title'>Edit Resource Profile Stage</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        label='Name'
                                        value={this.state.editStageName}
                                        onChange={event => this.setState({ editStageName: event.target.value })}
                                        margin='dense'
                                        variant='outlined'
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={this.handleEditDialogClose} color='default'>Cancel</Button>
                                    <Button onClick={this.handleEditSubmit} type='submit' color='primary' variant='contained' disabled={this.state.disabled}>
                                        Submit
                                    </Button>
                                </DialogActions>
                        </Dialog>
                        <Dialog
                            open={this.state.isProjectDialogOpen}
                            onClose={this.handleProjectDialogClose}
                            aria-labelledby='add-project-type-title'
                            fullWidth
                            maxWidth='xs'
                        >
                            <form onSubmit={this.handleProjectSubmit} noValidate>
                                <DialogTitle id='add-project-type-title'>Add Project Type</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        required
                                        label='Project Type'
                                        value={this.state.projectType}
                                        onChange={event => this.setState({ projectType: event.target.value })}
                                        margin='dense'
                                        variant='outlined'
                                        error={projectTypeMissing}
                                        helperText={projectTypeMissing ? 'Project type is required.' : ' '}
                                    />
                                    <TextField
                                        fullWidth
                                        required
                                        label='Value'
                                        value={this.state.projectValue}
                                        onChange={event => this.setState({ projectValue: event.target.value })}
                                        margin='dense'
                                        variant='outlined'
                                        error={projectValueMissing}
                                        helperText={projectValueMissing ? 'Value is required.' : ' '}
                                    />
                                    <Autocomplete
                                        options={this.state.disciplineData}
                                        getOptionLabel={option => option?.name || ''}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        value={this.state.projectDiscipline}
                                        onChange={(event, projectDiscipline) => this.setState({ projectDiscipline })}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                required
                                                label='Discipline'
                                                variant='outlined'
                                                margin='dense'
                                                error={this.state.projectFormSubmitted && !this.state.projectDiscipline}
                                                helperText={this.state.projectFormSubmitted && !this.state.projectDiscipline ? 'Discipline is required.' : ' '}
                                            />
                                        )}
                                    />
                                    <Autocomplete
                                        options={this.state.divisionData || []}
                                        getOptionLabel={option => option?.name || ''}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        value={this.state.projectDivision}
                                        onChange={(event, projectDivision) => this.setState({ projectDivision })}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                required
                                                label='Division'
                                                variant='outlined'
                                                margin='dense'
                                                error={projectDivisionMissing}
                                                helperText={projectDivisionMissing ? 'Division is required.' : ' '}
                                            />
                                        )}
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={this.handleProjectDialogClose} color='default'>Cancel</Button>
                                    <Button type='submit' color='primary' variant='contained' onClick={this.handleProjectDialogSubmit}>Submit</Button>
                                </DialogActions>
                            </form>
                        </Dialog>
                    </Box>
                )}
            </div>
        )
    }
}
export default withSnackbar(ResourceProfile);
